import { Dispatch, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SerializedError } from '@reduxjs/toolkit';
import { CartItemInput, CartItem } from '@pizza-hut-us-development/client-core';
import { CartItem as DomainCartItem} from '@/domain/cart/types';
import { onAddMultiStepDealToOrderButtonClick, onAddMultiStepDealToOrderSuccess } from '@/builders/deals/analytics';
import { Deal, DealData } from '@/builders/deals/slice/dealTypes';
import useCartToast from '@/cart/hooks/useCartToast';
import { transformCCCartItemToLegacy } from '@/cart/hooks/useAddToCart';
import { useCartAddItem as useCCCartAddItem } from '@/clientCore/cart/hooks/useCartAddItem';
import { getDealErrorModalDetails, NOT_COMBINABLE_DEALS_TITLE } from '@/common/Modal/modalDetails';
import { openCartToast } from '@/common/CartToast/slices/Toast.slice';
import { onAddToOrderButtonSuccess } from '@/dataAnalytics/dataAnalyticsHelper';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { openModal } from '@/localization/actions';

import { getOrInitializeOptimizely } from '../../../../optimizely/optimizely';
import { AddableCartItemKinds } from '@/api/phdApiV2Client/request.types';
import { MenuRecipe } from '@/menu/pizza/pizzaMenuTypes';

import useDeal, { Status } from '@/builders/deals/hooks/useDeal';
import { transformCartItemInputToDeal } from '@/clientCore/menu/utils/transformCartItemInputToDeal';

type UseQuickAddDealReturn = {
  quickAddDealToCart: (quickAddPayload: CartItemInput, deal: MenuRecipe) => void;
  dealIdCurrentlyAddingToCart: string | null;
}

const onQuickAddSuccess = (
  deal: Deal,
  cartItem: CartItem,
  setDealIdCurrentlyAddingToCart: (dealId: string | null) => void,
  shapeAndDisplayToast: (deal: Deal, cartItem: DomainCartItem, analytics: any) => void,
  dispatch: Dispatch<any>,
  analytics: any
) => {
  const isMultiStepDeal = deal.data.steps.length > 1;
  const isLineup = false;
  const optimizelyInstance = getOrInitializeOptimizely();

  analytics.push(() => (isMultiStepDeal
    ? onAddMultiStepDealToOrderSuccess(deal)
    : onAddToOrderButtonSuccess(deal, isLineup)));

  optimizelyInstance?.track('AddToOrder', { dealId: deal.data.id });
  setDealIdCurrentlyAddingToCart(null)
  shapeAndDisplayToast(
    deal,
    transformCCCartItemToLegacy(cartItem),
    false
  );
  dispatch(openCartToast());
}

const onQuickAddError = (
  error: SerializedError,
  setDealIdCurrentlyAddingToCart: (dealId: string | null) => void,
  dispatch: Dispatch<any>
) => {
  const dealErrorModalDetails = getDealErrorModalDetails(NOT_COMBINABLE_DEALS_TITLE, error.message ?? '');
  setDealIdCurrentlyAddingToCart(null);
  dispatch(openModal(dealErrorModalDetails));
};

const isDealData = (dealData: DealData | Status): dealData is DealData => (dealData as DealData).kind === AddableCartItemKinds.DEAL

export const useQuickAddDeal = (): UseQuickAddDealReturn => {
  const [quickAddPayload, setQuickAddPayload] = useState<CartItemInput | null>(null);
  const [dealIdCurrentlyAddingToCart, setDealIdCurrentlyAddingToCart] = useState<string | null>(null);
  const dispatch = useDispatch();
  const { shapeAndDisplayToast } = useCartToast();
  const { handleAddCartItem } = useCCCartAddItem();
  const analytics = useAnalytics();
  const { dealData } = useDeal(quickAddPayload?.id ?? '');

  const quickAddDealToCart = (payload: CartItemInput) => {
    setDealIdCurrentlyAddingToCart(payload.id);
    setQuickAddPayload(payload);
  }

  useEffect(() => {
    if (quickAddPayload?.id && dealData && isDealData(dealData) && dealData.id === quickAddPayload.id) {
      const transformedDeal = transformCartItemInputToDeal(quickAddPayload, dealData);
      analytics.push(() => onAddMultiStepDealToOrderButtonClick(transformedDeal));

      handleAddCartItem({
        item: quickAddPayload,
        onSuccess: (_, cartItem) => onQuickAddSuccess?.(transformedDeal, cartItem, setDealIdCurrentlyAddingToCart, shapeAndDisplayToast, dispatch, analytics),
        onError: (err) => {
          onQuickAddError(err, setDealIdCurrentlyAddingToCart, dispatch);
        }
      });
      
      setQuickAddPayload(null);
    }
  }, [analytics, dispatch, handleAddCartItem, quickAddPayload, shapeAndDisplayToast, dealData])

  return {
    quickAddDealToCart,
    dealIdCurrentlyAddingToCart
  }
}